export interface CorrelationFields {
  sessionId?: string;
  peerId?: string;
  trackId?: string;
}

const MAX_STRING_LENGTH = 120;
const MAX_OBJECT_KEYS = 8;
const MAX_OBJECT_DEPTH = 3;

export const compactString = (value: string, maxLength = MAX_STRING_LENGTH) =>
  value.length > maxLength ? `${value.slice(0, maxLength - 1)}...` : value;

const safeConstructorName = (value: object) => {
  try {
    return (value as { constructor?: { name?: string } }).constructor?.name;
  } catch (_error) {
    return undefined;
  }
};

const safeObjectKeys = (value: object) => {
  try {
    return Object.keys(value);
  } catch (_error) {
    return [];
  }
};

const safeObjectValue = (value: Record<string, unknown>, key: string) => {
  try {
    return value[key];
  } catch (error) {
    if (error instanceof Error) return `[Thrown ${error.name}: ${compactString(error.message)}]`;
    return '[Thrown while reading property]';
  }
};

const summarizeValueInternal = (
  value: unknown,
  seen: WeakSet<object>,
  depth: number,
): unknown => {
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') return compactString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return `${value.toString()}n`;
  if (typeof value === 'symbol') return value.toString();
  if (typeof value === 'function') return `[Function ${value.name || 'anonymous'}]`;
  if (value instanceof Error) return `${value.name}: ${compactString(value.message)}`;
  if (Array.isArray(value)) return `Array(${value.length})`;

  if (typeof value === 'object') {
    const object = value as Record<string, unknown>;
    const constructorName = safeConstructorName(object);
    if (seen.has(object)) return `[Circular ${constructorName || 'Object'}]`;

    const allKeys = safeObjectKeys(object);
    if (depth >= MAX_OBJECT_DEPTH) {
      return constructorName && constructorName !== 'Object'
        ? constructorName
        : `{${allKeys.length} keys}`;
    }

    const keys = allKeys.slice(0, MAX_OBJECT_KEYS);
    if (!keys.length) return constructorName && constructorName !== 'Object' ? constructorName : '{}';

    seen.add(object);
    const summary = keys.reduce(
      (result, key) => {
        result[key] = summarizeValueInternal(safeObjectValue(object, key), seen, depth + 1);
        return result;
      },
      {} as Record<string, unknown>,
    );
    seen.delete(object);
    if (allKeys.length > keys.length) summary.__truncatedKeys = allKeys.length - keys.length;
    if (constructorName && constructorName !== 'Object') summary.__type = constructorName;
    return summary;
  }

  return String(value);
};

export const summarizeValue = (value: unknown): unknown =>
  summarizeValueInternal(value, new WeakSet<object>(), 0);

export const summarizeArgs = (args: readonly unknown[]) => {
  const seen = new WeakSet<object>();
  return args.map((arg) => summarizeValueInternal(arg, seen, 0));
};

export const summarizeError = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack ? compactString(error.stack, 800) : undefined,
    };
  }

  return summarizeValue(error);
};

export const extractCorrelationFields = (...values: unknown[]): CorrelationFields => {
  for (const value of values) {
    if (!value || typeof value !== 'object') continue;
    const object = value as Record<string, unknown>;
    const fields = {
      sessionId: typeof object.sessionId === 'string' ? object.sessionId : undefined,
      peerId: typeof object.peerId === 'string' ? object.peerId : undefined,
      trackId: typeof object.trackId === 'string' ? object.trackId : undefined,
    };
    if (fields.sessionId || fields.peerId || fields.trackId) return fields;
  }

  return {};
};
